import JSZip from "jszip";

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function createZipAndDownload(zipName, filesByPath) {
  const zip = new JSZip();
  Object.entries(filesByPath || {}).forEach(([path, content]) => {
    if (!path) return;
    zip.file(path, typeof content === "string" ? content : String(content ?? ""));
  });
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(zipName, blob);
}

/**
 * ZIP entries from the IDE "Generated files" map (frontend/, backend/, nested folders).
 * Paths use forward slashes; JSZip infers directory structure from path segments.
 */
export function buildIdeProjectZipEntries(generatedFiles) {
  const src = generatedFiles && typeof generatedFiles === "object" ? generatedFiles : {};
  const out = {};
  for (const [rawPath, value] of Object.entries(src)) {
    const p = String(rawPath || "")
      .replace(/\\/g, "/")
      .replace(/^\/+/, "")
      .trim();
    if (!p) continue;
    out[p] = value == null ? "" : String(value);
  }
  return out;
}

export function buildStandaloneHtmlFromParts({ html = "", css = "", js = "", title = "Zolaid Export" }) {
  const isFullDoc = /<html[\s>]/i.test(html) || /<!doctype/i.test(html);
  if (isFullDoc) return html;
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${title}</title>
    <style>
${css}
    </style>
  </head>
  <body>
${html}
    <script>
${js}
    </script>
  </body>
</html>`;
}

export function buildStaticZipFiles({ htmlDoc, css = "", js = "", includeSplitFiles = true }) {
  const files = {
    "index.html": htmlDoc || "",
  };
  if (includeSplitFiles) {
    files["styles.css"] = css || "";
    files["app.js"] = js || "";
  }
  return files;
}

export function buildViteReactZipFiles({
  projectName = "zolaid-react-app",
  appJsx = "",
  appCss = "",
  mainJsx = "",
  extraFiles = {},
}) {
  const pkg = {
    name: projectName,
    private: true,
    version: "0.0.0",
    type: "module",
    scripts: {
      dev: "vite",
      build: "vite build",
      preview: "vite preview",
    },
    dependencies: {
      react: "^19.2.0",
      "react-dom": "^19.2.0",
    },
    devDependencies: {
      "@vitejs/plugin-react": "^4.6.2",
      vite: "^6.0.0",
    },
  };

  const files = {
    "package.json": JSON.stringify(pkg, null, 2),
    "vite.config.js": `import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";

export default defineConfig({
  plugins: [react()],
});
`,
    "index.html": `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${projectName}</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>
  </body>
</html>
`,
    "src/main.jsx":
      mainJsx ||
      `import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`,
    "src/App.jsx":
      appJsx ||
      `export default function App() {
  return <div style={{ padding: 24 }}>Hello from Zolaid</div>;
}
`,
    "src/App.css": appCss || "",
    "src/index.css": `:root { color-scheme: light; }
body { margin: 0; font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; }
`,
    "src/App.module.css": "",
    "README.md": `# ${projectName}

## Run locally
\`\`\`
npm install
npm run dev
\`\`\`
`,
    ...extraFiles,
  };

  // Clean empty placeholder file
  delete files["src/App.module.css"];

  return files;
}


